import React, { useState, useEffect, useRef, useCallback } from 'react';
import { 
  X, 
  Play, 
  FileText, 
  Download, 
  ChevronDown, 
  ChevronRight,
  CheckCircle,
  Lock,
  ArrowLeft
} from 'lucide-react';
import { coursesApi, uploadApi } from '../services/api';
import './CourseViewer.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Helper to convert stored URLs to current backend URL
// Handles legacy URLs from different preview environments
const normalizeVideoUrl = (url) => {
  if (!url) return null;
  
  // If it's a relative URL starting with /api, make it absolute
  if (url.startsWith('/api/')) {
    return `${API_URL}${url}`;
  }
  
  // If it contains any emergentagent.com URL, extract the path and use current backend
  const emergeMatch = url.match(/https?:\/\/[^/]+\.emergentagent\.com(\/api\/upload\/.*)/);
  if (emergeMatch) {
    return `${API_URL}${emergeMatch[1]}`;
  }

  // Handle emergent.host URLs - convert to relative path and use window.location.origin
  // This ensures videos work on custom domains like hyperdriveacademy.com
  const hostMatch = url.match(/https?:\/\/[^/]+\.emergent\.host(\/api\/upload\/.*)/);
  if (hostMatch) {
    // Use the current browser origin to ensure videos work on custom domains
    const origin = typeof window !== 'undefined' ? window.location.origin : API_URL;
    return `${origin}${hostMatch[1]}`;
  }
  
  // For YouTube, Vimeo, or other external URLs, return as-is
  return url;
};

// Helper to format description text with proper line breaks and bullet points
const formatDescription = (text) => {
  if (!text) return null;
  
  // Split by newlines and process each line
  const lines = text.split('\n');
  
  return lines.map((line, index) => {
    // Check if line starts with bullet point characters
    const bulletMatch = line.match(/^(\s*)([•\-\*])\s*(.*)$/);
    if (bulletMatch) {
      const [, indent, , content] = bulletMatch;
      return (
        <div key={index} className="description-bullet" style={{ paddingLeft: indent.length * 8 }}>
          <span className="bullet">•</span>
          <span>{content}</span>
        </div>
      );
    }
    
    // Check if line starts with a number (numbered list)
    const numberMatch = line.match(/^(\s*)(\d+[\.\)])\s*(.*)$/);
    if (numberMatch) {
      const [, indent, number, content] = numberMatch;
      return (
        <div key={index} className="description-numbered" style={{ paddingLeft: indent.length * 8 }}>
          <span className="number">{number}</span>
          <span>{content}</span>
        </div>
      );
    }
    
    // Empty line = paragraph break
    if (line.trim() === '') {
      return <div key={index} className="description-break"></div>;
    }
    
    // Regular line
    return <div key={index} className="description-line">{line}</div>;
  });
};

const CourseViewer = ({ courseId, onClose }) => {
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeEpisode, setActiveEpisode] = useState(null);
  const [expandedSections, setExpandedSections] = useState({});
  const [completedEpisodes, setCompletedEpisodes] = useState([]);
  const [videoProgress, setVideoProgress] = useState({}); // Map of episode_id -> {current_time, duration}
  const videoRef = useRef(null);
  const progressSaveInterval = useRef(null);
  const lastSavedTime = useRef(0);

  useEffect(() => {
    loadCourse();
    
    // Cleanup on unmount - save current progress
    return () => {
      if (progressSaveInterval.current) {
        clearInterval(progressSaveInterval.current);
      }
      // Save progress when leaving
      saveCurrentProgress();
    };
  }, [courseId]);

  // Save current video progress
  const saveCurrentProgress = useCallback(async () => {
    if (!videoRef.current || !activeEpisode) return;
    
    const video = videoRef.current;
    const currentTime = video.currentTime;
    const duration = video.duration;
    
    // Don't save if video hasn't loaded or time is too early
    if (!duration || currentTime < 5) return;
    
    // Don't save if we're near the end (within 10 seconds) - consider it complete
    if (duration - currentTime < 10) return;
    
    // Only save if we've moved at least 5 seconds since last save
    if (Math.abs(currentTime - lastSavedTime.current) < 5) return;
    
    try {
      await coursesApi.saveProgress(activeEpisode.id, currentTime, duration);
      lastSavedTime.current = currentTime;
      
      // Update local state
      setVideoProgress(prev => ({
        ...prev,
        [activeEpisode.id]: { current_time: currentTime, duration }
      }));
    } catch (error) {
      console.error('Failed to save progress:', error);
    }
  }, [activeEpisode]);

  // Set up interval to save progress periodically
  useEffect(() => {
    if (activeEpisode && videoRef.current) {
      // Clear any existing interval
      if (progressSaveInterval.current) {
        clearInterval(progressSaveInterval.current);
      }
      
      // Save progress every 10 seconds while watching
      progressSaveInterval.current = setInterval(() => {
        saveCurrentProgress();
      }, 10000);
      
      return () => {
        if (progressSaveInterval.current) {
          clearInterval(progressSaveInterval.current);
        }
      };
    }
  }, [activeEpisode, saveCurrentProgress]);

  const loadCourse = async () => {
    try {
      const courseData = await coursesApi.get(courseId);
      setCourse(courseData);
      
      // Load completions and progress first to determine where to start
      let completedList = [];
      let progressData = {};
      
      try {
        const completionsResult = await coursesApi.getCompletions(courseId);
        completedList = completionsResult.completed_episodes || [];
        setCompletedEpisodes(completedList);
      } catch (e) {
        // Use localStorage fallback
        const saved = localStorage.getItem(`course_progress_${courseId}`);
        if (saved) {
          completedList = JSON.parse(saved);
          setCompletedEpisodes(completedList);
        }
      }
      
      try {
        const progressResult = await coursesApi.getAllProgress();
        progressData = progressResult.progress || {};
        setVideoProgress(progressData);
      } catch (e) {
        console.error('Failed to load progress:', e);
      }
      
      // Find the best episode to start with:
      // 1. First episode with saved progress (resume watching)
      // 2. First incomplete episode (continue course)
      // 3. First episode (start fresh)
      if (courseData.sections?.length > 0) {
        let targetEpisode = null;
        let targetSection = null;
        
        // First, look for an episode with saved progress
        for (const section of courseData.sections) {
          for (const episode of (section.episodes || [])) {
            if (progressData[episode.id] && progressData[episode.id].current_time > 5) {
              targetEpisode = episode;
              targetSection = section;
              break;
            }
          }
          if (targetEpisode) break;
        }
        
        // If no in-progress episode, find first incomplete
        if (!targetEpisode) {
          for (const section of courseData.sections) {
            for (const episode of (section.episodes || [])) {
              if (!completedList.includes(episode.id)) {
                targetEpisode = episode;
                targetSection = section;
                break;
              }
            }
            if (targetEpisode) break;
          }
        }
        
        // Fallback to first episode
        if (!targetEpisode) {
          targetSection = courseData.sections[0];
          targetEpisode = targetSection.episodes?.[0];
        }
        
        if (targetEpisode && targetSection) {
          setExpandedSections({ [targetSection.id]: true });
          setActiveEpisode({
            ...targetEpisode,
            sectionId: targetSection.id,
            sectionTitle: targetSection.title
          });
        }
      }
    } catch (error) {
      console.error('Failed to load course:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleSection = (sectionId) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  const selectEpisode = (episode, section) => {
    // Save progress of current episode before switching
    saveCurrentProgress();
    
    // Reset last saved time for new episode
    lastSavedTime.current = 0;
    
    // Expand the section if it's not already expanded
    if (!expandedSections[section.id]) {
      setExpandedSections(prev => ({
        ...prev,
        [section.id]: true
      }));
    }
    
    setActiveEpisode({
      ...episode,
      sectionId: section.id,
      sectionTitle: section.title
    });
  };

  // Helper function to get the next episode
  const getNextEpisode = () => {
    if (!course?.sections || !activeEpisode) return null;
    
    let foundCurrent = false;
    for (const section of course.sections) {
      for (const episode of (section.episodes || [])) {
        if (foundCurrent) {
          // Return the next episode
          return { episode, section };
        }
        if (episode.id === activeEpisode.id) {
          foundCurrent = true;
        }
      }
    }
    return null; // No next episode (we're at the end)
  };

  const markAsComplete = async (episodeId) => {
    const isAlreadyCompleted = completedEpisodes.includes(episodeId);
    
    try {
      // Save to database
      await coursesApi.markComplete(courseId, episodeId, !isAlreadyCompleted);
      
      // Update local state
      const updated = isAlreadyCompleted
        ? completedEpisodes.filter(id => id !== episodeId)
        : [...completedEpisodes, episodeId];
      
      setCompletedEpisodes(updated);
      
      // Also update localStorage as backup
      localStorage.setItem(`course_progress_${courseId}`, JSON.stringify(updated));
      
      // Clear video progress for this episode since it's complete
      if (!isAlreadyCompleted) {
        setVideoProgress(prev => {
          const newProgress = { ...prev };
          delete newProgress[episodeId];
          return newProgress;
        });
      }
      
      // Auto-advance to next episode only when marking as complete (not when uncompleting)
      if (!isAlreadyCompleted) {
        const next = getNextEpisode();
        if (next) {
          // Small delay for UX - let user see the completion animation
          setTimeout(() => {
            selectEpisode(next.episode, next.section);
          }, 500);
        }
      }
    } catch (error) {
      console.error('Failed to update completion:', error);
    }
  };

  const getProgress = () => {
    if (!course?.sections) return 0;
    const totalEpisodes = course.sections.reduce((acc, s) => acc + (s.episodes?.length || 0), 0);
    if (totalEpisodes === 0) return 0;
    return Math.round((completedEpisodes.length / totalEpisodes) * 100);
  };

  const renderVideo = (videoUrl) => {
    if (!videoUrl) return null;

    // Normalize the video URL to use current backend
    const normalizedUrl = normalizeVideoUrl(videoUrl);
    if (!normalizedUrl) return null;

    // Check if it's a YouTube URL
    const youtubeMatch = normalizedUrl.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
    if (youtubeMatch) {
      return (
        <iframe
          src={`https://www.youtube.com/embed/${youtubeMatch[1]}`}
          title="Course Video"
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      );
    }

    // Check if it's a Vimeo URL
    const vimeoMatch = normalizedUrl.match(/vimeo\.com\/(\d+)/);
    if (vimeoMatch) {
      return (
        <iframe
          src={`https://player.vimeo.com/video/${vimeoMatch[1]}`}
          title="Course Video"
          frameBorder="0"
          allow="autoplay; fullscreen; picture-in-picture"
          allowFullScreen
        />
      );
    }

    // Otherwise, treat as direct video file
    // Get saved progress for this episode
    const savedProgress = activeEpisode ? videoProgress[activeEpisode.id] : null;
    
    return (
      <video 
        ref={videoRef}
        key={normalizedUrl}
        controls 
        preload="metadata"
        playsInline
        style={{ backgroundColor: '#000', width: '100%' }}
        onError={(e) => {
          // Only handle error if the video networkState indicates a real error
          // networkState: 0 = EMPTY, 1 = IDLE, 2 = LOADING, 3 = NO_SOURCE
          const video = e.target;
          if (video.networkState === 3 || (video.error && video.error.code)) {
            console.error('Video failed to load:', normalizedUrl, video.error);
            video.style.display = 'none';
            const parent = video.parentElement;
            if (parent && !parent.querySelector('.video-error')) {
              const errorDiv = document.createElement('div');
              errorDiv.className = 'video-error';
              errorDiv.innerHTML = '<p>Video unavailable</p><p style="font-size:12px;opacity:0.7;">This video may need to be re-uploaded by the admin.</p>';
              errorDiv.style.cssText = 'display:flex;flex-direction:column;align-items:center;justify-content:center;height:100%;min-height:300px;background:#1a1a2e;color:white;text-align:center;padding:20px;';
              parent.appendChild(errorDiv);
            }
          }
        }}
        onLoadedMetadata={(e) => {
          console.log('Video metadata loaded, duration:', e.target.duration);
          // Resume from saved position if available
          if (savedProgress && savedProgress.current_time > 5) {
            const video = e.target;
            // Only resume if we're not near the end
            if (savedProgress.current_time < (video.duration - 10)) {
              video.currentTime = savedProgress.current_time;
              console.log('Resuming from:', savedProgress.current_time);
            }
          }
        }}
        onPause={() => {
          // Save progress when user pauses
          saveCurrentProgress();
        }}
        onEnded={() => {
          // Auto-mark episode as complete when video finishes
          if (activeEpisode && !completedEpisodes.includes(activeEpisode.id)) {
            markAsComplete(activeEpisode.id);
          }
        }}
      >
        <source src={normalizedUrl} type="video/mp4" />
        Your browser does not support the video tag.
      </video>
    );
  };

  // Handle close with progress save
  const handleClose = async () => {
    await saveCurrentProgress();
    onClose();
  };

  if (loading) {
    return (
      <div className="course-viewer-overlay">
        <div className="course-viewer-loading">
          <div className="loader"></div>
          <p>Loading course...</p>
        </div>
      </div>
    );
  }

  if (!course) {
    return (
      <div className="course-viewer-overlay">
        <div className="course-viewer-error">
          <p>Course not found</p>
          <button onClick={handleClose}>Close</button>
        </div>
      </div>
    );
  }

  return (
    <div className="course-viewer-overlay">
      <div className="course-viewer">
        {/* Header */}
        <div className="course-viewer-header">
          <button className="back-btn" onClick={handleClose}>
            <ArrowLeft size={20} />
            <span>Back to Courses</span>
          </button>
          <div className="course-title-header">
            <h1>{course.title}</h1>
            <span className="progress-badge">{getProgress()}% Complete</span>
          </div>
          <button className="close-btn" onClick={handleClose}>
            <X size={24} />
          </button>
        </div>

        <div className="course-viewer-content">
          {/* Sidebar - Course Curriculum */}
          <aside className="course-sidebar">
            <div className="sidebar-header">
              <h2>Course Content</h2>
              <div className="progress-bar">
                <div className="progress-fill" style={{ width: `${getProgress()}%` }} />
              </div>
            </div>

            <div className="sections-list">
              {course.sections?.map((section, sectionIndex) => (
                <div key={section.id} className="section-block">
                  <button 
                    className="section-toggle"
                    onClick={() => toggleSection(section.id)}
                  >
                    <div className="section-info">
                      {expandedSections[section.id] ? (
                        <ChevronDown size={18} />
                      ) : (
                        <ChevronRight size={18} />
                      )}
                      <span className="section-number">Section {sectionIndex + 1}</span>
                      <span className="section-title">{section.title}</span>
                    </div>
                    <span className="episode-count">
                      {section.episodes?.filter(e => completedEpisodes.includes(e.id)).length || 0}/
                      {section.episodes?.length || 0}
                    </span>
                  </button>

                  {expandedSections[section.id] && (
                    <div className="episodes-list">
                      {section.episodes?.map((episode, episodeIndex) => {
                        const episodeProgress = videoProgress[episode.id];
                        const progressPercent = episodeProgress && episodeProgress.duration > 0 
                          ? Math.round((episodeProgress.current_time / episodeProgress.duration) * 100)
                          : 0;
                        const isCompleted = completedEpisodes.includes(episode.id);
                        const hasProgress = progressPercent > 0 && progressPercent < 95;
                        
                        return (
                          <button
                            key={episode.id}
                            className={`episode-item ${activeEpisode?.id === episode.id ? 'active' : ''} ${isCompleted ? 'completed' : ''} ${hasProgress ? 'in-progress' : ''}`}
                            onClick={() => selectEpisode(episode, section)}
                          >
                            <div className="episode-status">
                              {isCompleted ? (
                                <CheckCircle size={16} className="completed-icon" />
                              ) : hasProgress ? (
                                <div className="progress-circle">
                                  <svg viewBox="0 0 36 36" className="circular-progress">
                                    <path
                                      className="circle-bg"
                                      d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                                    />
                                    <path
                                      className="circle-fill"
                                      strokeDasharray={`${progressPercent}, 100`}
                                      d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                                    />
                                  </svg>
                                </div>
                              ) : (
                                <span className="episode-number">{episodeIndex + 1}</span>
                              )}
                            </div>
                            <div className="episode-info">
                              <span className="episode-title">{episode.title}</span>
                              <div className="episode-meta">
                                {episode.video_url && <Play size={12} />}
                                {episode.attachments?.length > 0 && (
                                  <span><FileText size={12} /> {episode.attachments.length}</span>
                                )}
                                {hasProgress && (
                                  <span className="resume-indicator">Resume • {progressPercent}%</span>
                                )}
                                {isCompleted && (
                                  <span className="completed-indicator">Completed</span>
                                )}
                              </div>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </aside>

          {/* Main Content - Video Player & Episode Details */}
          <main className="course-main">
            {activeEpisode ? (
              <>
                {/* Video Player - contained width */}
                <div className="video-wrapper">
                  <div className="video-container" key={`video-${activeEpisode.id}`}>
                    {activeEpisode.video_url ? (
                      renderVideo(activeEpisode.video_url)
                    ) : (
                      <div className="no-video">
                        <Play size={48} />
                        <p>No video available for this episode</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Episode Info */}
                <div className="episode-details-panel">
                  <div className="episode-header">
                    <div>
                      <span className="section-label">{activeEpisode.sectionTitle}</span>
                      <h2>{activeEpisode.title}</h2>
                    </div>
                    <button 
                      className={`mark-complete-btn ${completedEpisodes.includes(activeEpisode.id) ? 'completed' : ''}`}
                      onClick={() => markAsComplete(activeEpisode.id)}
                    >
                      <CheckCircle size={18} />
                      {completedEpisodes.includes(activeEpisode.id) ? 'Completed' : 'Mark as Complete'}
                    </button>
                  </div>

                  {activeEpisode.description && (
                    <div className="episode-description">
                      <h3>About this lesson</h3>
                      <div className="description-content">
                        {formatDescription(activeEpisode.description)}
                      </div>
                    </div>
                  )}

                  {/* Attachments */}
                  {activeEpisode.attachments?.length > 0 && (
                    <div className="episode-attachments">
                      <h3>Resources & Downloads</h3>
                      <div className="attachments-grid">
                        {activeEpisode.attachments.map(attachment => (
                          <a 
                            key={attachment.id}
                            href={attachment.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="attachment-card"
                          >
                            <FileText size={24} />
                            <div className="attachment-info">
                              <span className="attachment-name">{attachment.name}</span>
                              <span className="attachment-size">{attachment.size}</span>
                            </div>
                            <Download size={18} />
                          </a>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <div className="no-episode-selected">
                <Play size={64} />
                <h2>Select an episode to start learning</h2>
                <p>Choose an episode from the sidebar to begin</p>
              </div>
            )}
          </main>
        </div>
      </div>
    </div>
  );
};

export default CourseViewer;
